import { bffAxios } from "@av/config/api-config/axios-config";
import { PDF_CONTENT_TYPE } from "./constants";

import type {
  ApiDocumentResponse,
  CompanyDocumentUpload,
  CompanyDocumentView,
  CompanyKnowledgeDocument,
} from "./types";

export function getPdfContentType(file: Pick<File, "type">) {
  return file.type === PDF_CONTENT_TYPE ? file.type : PDF_CONTENT_TYPE;
}

export async function uploadCompanyDocumentFile(
  file: Blob,
  upload: CompanyDocumentUpload,
  contentType = getPdfContentType(file)
) {
  if (upload.method.toUpperCase() !== "PUT") {
    throw new Error(`Unsupported company document upload method: ${upload.method}`);
  }

  await bffAxios.put(upload.presignedUrl, file, {
    headers: { "Content-Type": contentType },
  });
}

export async function requestCompanyDocumentFileReplacement({
  contentType,
  documentId,
  fileSizeBytes,
  filename,
}: {
  contentType: string;
  documentId: string;
  fileSizeBytes: number;
  filename: string;
}) {
  const { data } = await bffAxios.put<ApiDocumentResponse<CompanyDocumentUpload>>(
    `/company-documents/${documentId}/file`,
    {
      filename,
      contentType,
      fileSizeBytes,
    }
  );

  return data.data;
}

export async function fetchCompanyDocumentBlob(documentId: string) {
  const { data } = await bffAxios.get<ApiDocumentResponse<{ presignedUrl: string }>>(
    `/company-documents/${documentId}`
  );
  const response = await bffAxios.get<Blob>(data.data.presignedUrl, { responseType: "blob" });

  return response.data;
}

export async function rollbackCreatedCompanyDocument(documentId: string) {
  await bffAxios.delete(`/company-documents/${documentId}`).catch(() => undefined);
}

export async function enableCompanyDocumentInAva(documentId: string) {
  const { data } = await bffAxios.patch<ApiDocumentResponse<CompanyDocumentView>>(
    `/company-documents/${documentId}`,
    { useInAva: true }
  );

  return data.data;
}

export async function restorePreviousCompanyDocumentFile(
  document: CompanyKnowledgeDocument,
  previousFile: Blob
) {
  const upload = await requestCompanyDocumentFileReplacement({
    contentType: document.contentType,
    documentId: document.id,
    filename: document.fileName,
    fileSizeBytes: document.fileSizeBytes,
  });

  await uploadCompanyDocumentFile(previousFile, upload, document.contentType);

  if (document.useInAva) {
    await enableCompanyDocumentInAva(document.id);
  }
}
